import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { useChatStreamStatus } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatStreamStatusContext'
import {
  buildCancelStagedActionMessage,
  buildConfirmStagedActionMessage,
} from './stagedActionMessages'

const Card = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${rem(16)};
  background: #fff;
  padding: ${rem(16)};
`

const Title = styled(Heading)`
  display: block;
  margin-bottom: ${rem(8)};
`

const Body = styled(BodyText)`
  display: block;
  margin-bottom: ${rem(16)};
`

const PreviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  margin-bottom: ${rem(16)};
`

const PreviewPanel = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${rem(12)};
  background: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(16)};
`

const PreviewRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(14)};
  align-items: start;
`

const PreviewSummary = styled(BodyText)`
  display: block;
  overflow-wrap: anywhere;
`

const PreviewSummaryLabel = styled(BodyText)`
  margin-inline-end: ${rem(4)};
  font-weight: 700;
`

const PreviewValues = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`

const PreviewValueGroup = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${rem(12)};
  align-items: baseline;
  justify-content: space-between;
  padding: ${rem(10)} 0;

  & + & {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const PreviewValueLabel = styled(BodyText)`
  display: block;
`

const PreviewStatusPill = styled.div<{ $tone: 'current' | 'afterPositive' | 'afterNegative' }>`
  min-width: ${rem(156)};
  padding: ${rem(7)} ${rem(16)};
  border-radius: ${rem(8)};
  background: ${props => {
    if (props.$tone === 'afterNegative') {
      return props.theme.palette.messaging.critical.lightBackground
    }

    if (props.$tone === 'afterPositive') {
      return props.theme.palette.messaging.success.lightBackground
    }

    return props.theme.palette.neutral.default.seven
  }};
  color: ${props => {
    if (props.$tone === 'afterNegative') {
      return props.theme.palette.messaging.critical.lightContrast
    }

    if (props.$tone === 'afterPositive') {
      return props.theme.palette.messaging.success.lightContrast
    }

    return props.theme.palette.neutral.default.one
  }};
  text-align: center;
`

const Actions = styled.div`
  display: flex;
  gap: ${rem(8)};
  flex-wrap: wrap;
`

const HistoryToggle = styled.div`
  margin-top: ${rem(12)};
`

const Footnote = styled.div`
  display: flex;
  gap: ${rem(8)};
  align-items: flex-start;
  margin-bottom: ${rem(16)};
`

export interface CommunicationsPreferencesConfirmationPromptProps {
  pendingActionId: string
  selectedAction?: 'confirm' | 'cancel'
  isHistorical?: boolean
  previewRows: Array<{
    label: string
    currentValue: string
    nextValue: string
  }>
  onSendMessage: (message: string) => void
}

type PreviewRowGroup = {
  labels: Array<string>
  currentValue: string
  nextValue: string
}

function groupPreviewRows(
  previewRows: CommunicationsPreferencesConfirmationPromptProps['previewRows'],
): Array<PreviewRowGroup> {
  const groups = new Map<string, PreviewRowGroup>()

  previewRows
    .forEach((row) => {
      const key = `${row.currentValue}:::${row.nextValue}`
      const existing = groups.get(key)

      if (existing) {
        existing.labels.push(row.label)
        return
      }

      groups.set(key, {
        labels: [row.label],
        currentValue: row.currentValue,
        nextValue: row.nextValue,
      })
    })

  return Array.from(groups.values())
}

function getAfterTone(nextValue: string): 'afterPositive' | 'afterNegative' {
  return nextValue.toLowerCase() === 'unsubscribed' ? 'afterNegative' : 'afterPositive'
}

function shouldCollapseHistory({
  isHistorical,
  isExpanded,
  selectedAction,
}: {
  isHistorical: boolean
  isExpanded: boolean
  selectedAction?: 'confirm' | 'cancel'
}): boolean {
  return isHistorical && !isExpanded && !selectedAction
}

function formatChangingLabel(label: string, index: number): string {
  if (label === 'SMS') {
    return label
  }

  if (index === 0) {
    return label
  }

  return `${label.charAt(0).toLocaleLowerCase()}${label.slice(1)}`
}

function CommunicationsPreferencesConfirmationPrompt({
  pendingActionId,
  selectedAction,
  isHistorical = false,
  previewRows,
  onSendMessage,
}: CommunicationsPreferencesConfirmationPromptProps) {
  const intl = useIntl()
  const { isStreaming } = useChatStreamStatus()
  const [isExpanded, setIsExpanded] = useState(() => !(isHistorical && !selectedAction))
  const labels = {
    promptTitle: intl.formatMessage({
      id: 'stDN0BNPBVpor3HZO0TP',
      defaultMessage: 'Confirm your changes',
      description: 'Title shown in the communications preferences confirmation prompt',
    }),
    promptBody: intl.formatMessage({
      id: 'OVyRMwUEkhLV05BeAkEA',
      defaultMessage: 'Please confirm before we save this communication preference change.',
      description: 'Body shown in the communications preferences confirmation prompt',
    }),
    confirmButton: intl.formatMessage({
      id: 'CEi5Ds221gUN1ECaMVJa',
      defaultMessage: 'Confirm',
      description: 'Primary action button label in the communications preferences confirmation prompt',
    }),
    cancelButton: intl.formatMessage({
      id: 'QH-s7uvSIqnbX4h4lawC',
      defaultMessage: 'Cancel',
      description: 'Secondary action button label in the communications preferences confirmation prompt',
    }),
    footnote: intl.formatMessage({
      id: 'hLu8c2eNh6pxd8WomCdx',
      defaultMessage: 'You can change this again at any time in your communication preferences.',
      description: 'Supporting note shown below the communications preferences confirmation prompt',
    }),
    currentValueLabel: intl.formatMessage({
      id: 'nSQxHr4_CsPC480s7yz7',
      defaultMessage: 'Currently',
      description: 'Label for the current communication preference value in the confirmation prompt',
    }),
    nextValueLabel: intl.formatMessage({
      id: '4JWyZrL8w1XnSGkPF527',
      defaultMessage: 'After change',
      description: 'Label for the next communication preference value in the confirmation prompt',
    }),
    changingLabel: intl.formatMessage({
      id: 'Dv41a6eXHF5u144ilKq_',
      defaultMessage: 'Changing:',
      description: 'Label shown before the list of communication preferences being changed',
    }),
    showOptionsLabel: intl.formatMessage({
      id: 'EqEfSM9iAmDHUMOTcSsK',
      defaultMessage: 'Show options',
      description: 'Locked-history pill label that expands pill options when nothing was selected in trip planner chat',
    }),
    hideOptionsLabel: intl.formatMessage({
      id: 'RmZa65U-wl0kYCWqCnn4',
      defaultMessage: 'Hide options',
      description: 'Locked-history pill label that collapses pill options when there was no prior selection in trip planner chat',
    }),
  }
  const groupedPreviewRows = groupPreviewRows(previewRows)

  useEffect(() => {
    setIsExpanded(!(isHistorical && !selectedAction))
  }, [isHistorical, selectedAction])

  const isHistoryCollapsed = shouldCollapseHistory({
    isHistorical,
    isExpanded,
    selectedAction,
  })

  if (selectedAction) {
    return (
      <Actions>
        <TextButton
          kind={selectedAction === 'confirm' ? 'primary' : 'secondary'}
          size="medium"
          disabled
        >
          {selectedAction === 'confirm' ? labels.confirmButton : labels.cancelButton}
        </TextButton>
      </Actions>
    )
  }

  if (isHistoryCollapsed) {
    return (
      <TextButton
        kind="tertiary"
        size="small"
        onClick={() => setIsExpanded(true)}
      >
        {labels.showOptionsLabel}
      </TextButton>
    )
  }

  return (
    <Card>
      <Title variant="heading6" colour="neutral-one">
        {labels.promptTitle}
      </Title>
      <Body variant="medium" colour="neutral-one">
        {labels.promptBody}
      </Body>

      {groupedPreviewRows.length > 0 && (
        <PreviewList>
          {groupedPreviewRows.map((group) => (
            <PreviewPanel key={`${group.currentValue}-${group.nextValue}-${group.labels.join('|')}`}>
              <PreviewRow>
                <PreviewSummary variant="medium" colour="neutral-one">
                  <PreviewSummaryLabel as="span" variant="medium" colour="inherit">
                    {labels.changingLabel}
                  </PreviewSummaryLabel>
                  {group.labels.map(formatChangingLabel).join(', ')}
                </PreviewSummary>
                <PreviewValues>
                  <PreviewValueGroup>
                    <PreviewValueLabel variant="medium" colour="neutral-two">
                      {labels.currentValueLabel}
                    </PreviewValueLabel>
                    <PreviewStatusPill $tone="current">
                      <BodyText variant="medium" colour="inherit" weight="bold">
                        {group.currentValue}
                      </BodyText>
                    </PreviewStatusPill>
                  </PreviewValueGroup>
                  <PreviewValueGroup>
                    <PreviewValueLabel variant="medium" colour="neutral-two">
                      {labels.nextValueLabel}
                    </PreviewValueLabel>
                    <PreviewStatusPill $tone={getAfterTone(group.nextValue)}>
                      <BodyText variant="medium" colour="inherit" weight="bold">
                        {group.nextValue}
                      </BodyText>
                    </PreviewStatusPill>
                  </PreviewValueGroup>
                </PreviewValues>
              </PreviewRow>
            </PreviewPanel>
          ))}
        </PreviewList>
      )}

      <Footnote>
        <LineInfoCircleIcon size={18} colour="neutral-three" />
        <BodyText variant="medium" colour="neutral-three">
          {labels.footnote}
        </BodyText>
      </Footnote>

      <Actions>
        <TextButton
          kind="primary"
          size="medium"
          disabled={isHistorical || isStreaming}
          onClick={() => onSendMessage(
            buildConfirmStagedActionMessage(pendingActionId),
          )}
        >
          {labels.confirmButton}
        </TextButton>
        <TextButton
          kind="secondary"
          size="medium"
          disabled={isHistorical || isStreaming}
          onClick={() => onSendMessage(
            buildCancelStagedActionMessage(pendingActionId),
          )}
        >
          {labels.cancelButton}
        </TextButton>
      </Actions>

      {isHistorical && (
        <HistoryToggle>
          <TextButton
            kind="tertiary"
            size="small"
            onClick={() => setIsExpanded(false)}
          >
            {labels.hideOptionsLabel}
          </TextButton>
        </HistoryToggle>
      )}
    </Card>
  )
}

export default CommunicationsPreferencesConfirmationPrompt
